"use client";

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Calendar, Clock, ArrowRight } from "lucide-react";

const ContactFollowUpModal = ({ isOpen, onClose }) => {
  const [showCalendar, setShowCalendar] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);



  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowCalendar(false);
    }
  }, [isOpen]);

  const handleMeetWithUs = useCallback(() => {
    setShowCalendar(true);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
            onClick={onClose}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.4, type: "spring", bounce: 0.2 }}
            className="fixed inset-0 flex items-center justify-center z-[101] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <motion.div
              layout
              className={`relative w-full bg-white rounded-3xl shadow-2xl overflow-hidden ${
                showCalendar ? "max-w-7xl" : "max-w-md"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-4 h-4 text-neutral-600" />
              </motion.button>

              <AnimatePresence mode="wait">
                {!showCalendar ? (
                  /* Initial Prompt View */
                  <motion.div
                    key="prompt"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 py-10 sm:px-10 sm:py-12"
                  >
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          bounce: 0.5,
                          delay: 0.15,
                        }}
                        className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25"
                      >
                        <Clock className="w-8 h-8 text-white" />
                      </motion.div>
                    </div>

                    {/* Heading */}
                    <motion.h3
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-2xl sm:text-3xl font-bold text-neutral-950 text-center tracking-tight"
                    >
                      Short on time?
                    </motion.h3>

                    {/* Subheading */}
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="mt-3 text-base text-neutral-500 text-center leading-relaxed max-w-sm mx-auto"
                    >
                      Skip the form and book a quick call with our team
                      instead. We&apos;ll discuss your project live.
                    </motion.p>

                    {/* Benefits */}
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="mt-6 flex flex-col gap-2"
                    >
                      {[
                        "Free 15-minute consultation",
                        "Get instant answers to your questions",
                        "No commitment required",
                      ].map((benefit, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 text-sm text-neutral-600"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                          {benefit}
                        </div>
                      ))}
                    </motion.div>

                    {/* CTA Button */}
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="mt-8 flex flex-col gap-3"
                    >
                      <button
                        onClick={handleMeetWithUs}
                        className="group w-full flex items-center justify-center gap-2 px-6 py-4 bg-neutral-950 text-white rounded-2xl text-base font-semibold hover:bg-neutral-800 transition-all duration-200 shadow-lg shadow-neutral-950/10"
                      >
                        <Calendar className="w-5 h-5" />
                        Meet With Us
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </button>
                      <button
                        onClick={onClose}
                        className="w-full px-6 py-3 text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
                      >
                        I&apos;ll fill out the form instead
                      </button>
                    </motion.div>
                  </motion.div>
                ) : (
                  /* Calendar View */
                  <motion.div
                    key="calendar"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pt-8 pb-6 sm:px-8"
                  >
                    {/* Calendar Header */}
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-neutral-950 tracking-tight">
                        Pick a time that works for you
                      </h3>
                      <p className="mt-1 text-sm text-neutral-500">
                        Select a slot below to book your free consultation
                      </p>
                    </div>

                    {/* Calendar Embed */}
                    <div className="w-full h-[70vh] sm:h-[650px] overflow-hidden rounded-lg border border-neutral-200 bg-white">
                      <iframe
                        src="https://book.kingdomkode.com/team/kingdom-kode/15-minute-consultation"
                        className="w-full h-full"
                        style={{ border: 0 }}
                        title="Book a consultation"
                      />
                    </div>

                    {/* Back link */}
                    <button
                      onClick={() => setShowCalendar(false)}
                      className="mt-4 w-full text-center text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      Back to message
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactFollowUpModal;
